import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { CheckRounded, VerifiedUserOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import utils from "../../universal/utils";
import { colors } from "../../theme/colors";
import {
    ACCOUNT_PAGE_KYC_BADGE_SX,
    ACCOUNT_PAGE_KYC_CTA_SX,
    ACCOUNT_PAGE_KYC_STATUS_CHIP_SX,
    ACCOUNT_PAGE_KYC_SUCCESS_PANEL_SX,
    ACCOUNT_PAGE_KYC_SX,
    ACCOUNT_PAGE_KYC_VERIFIED_SX,
    ACCOUNT_PAGE_MOBILE_MQ,
} from "./accountPageStyles";

const basePath = utils.getEdgeBasePath();

const STATUS_COPY = {
    approved: {
        label: "Verified",
        message: "Your identity has been verified. A Verified badge will appear on your profile.",
    },
    in_review: {
        label: "Under review",
        message: "Your verification was submitted and is being reviewed. The Verified badge is added only after approval. You will be notified when a decision has been made.",
    },
    in_progress: {
        label: "In progress",
        message: "Your verification is in progress. If you closed the window, you can continue below.",
    },
    declined: {
        label: "Declined",
        message: "Your verification was declined. You may try again if you believe this is a mistake.",
    },
    abandoned: {
        label: "Incomplete",
        message: "Your previous verification was not completed. You can start a new one.",
    },
    expired: {
        label: "Expired",
        message: "Your previous verification session expired. You can start a new one.",
    },
};

function getCtaLabel(status) {
    if (!status || status === "not_started") return "Start identity verification";
    if (status === "in_progress") return "Continue verification";
    if (status === "declined" || status === "abandoned" || status === "expired") {
        return "Retry verification";
    }
    return null;
}

function AccountIdentityVerification() {
    const [kycStatus, setKycStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await fetch(`${basePath}/api/v1/account/kyc/status`, {
                credentials: "include",
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.ok) {
                setKycStatus(data.kyc?.status || null);
            } else {
                setKycStatus(null);
            }
        } catch (_) {
            setKycStatus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    async function handleStartVerification() {
        setStarting(true);
        setError(null);
        try {
            const res = await fetch(`${basePath}/api/v1/account/kyc/didit/session`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.ok && data?.verificationUrl) {
                window.location.href = data.verificationUrl;
                return;
            }
            setError(data?.message || "Could not start verification. Please try again.");
        } catch (_) {
            setError("Could not start verification. Please check your connection and try again.");
        } finally {
            setStarting(false);
        }
    }

    const meta = STATUS_COPY[kycStatus] || null;
    const ctaLabel = getCtaLabel(kycStatus);
    const isApproved = kycStatus === "approved";

    return (
        <Box sx={[ACCOUNT_PAGE_KYC_SX, isApproved ? ACCOUNT_PAGE_KYC_VERIFIED_SX : null]}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.85rem",
                    mb: isApproved ? "1rem" : "0.85rem",
                    [ACCOUNT_PAGE_MOBILE_MQ]: {
                        alignItems: "flex-start",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.7rem",
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: "2.15rem",
                            height: "2.15rem",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            background: isApproved
                                ? "rgba(34, 197, 94, 0.12)"
                                : alpha(colors.primary.main, 0.1),
                            border: isApproved
                                ? "1px solid rgba(34, 197, 94, 0.28)"
                                : `1px solid ${alpha(colors.primary.main, 0.18)}`,
                            color: isApproved
                                ? colors.state.success.main
                                : colors.primary.main,
                        }}
                    >
                        <VerifiedUserOutlined sx={{ fontSize: "1.15rem" }} />
                    </Box>
                    <Box
                        sx={{
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "0.7rem",
                            [ACCOUNT_PAGE_MOBILE_MQ]: {
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "0.35rem",
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                color: colors.text.primary,
                                fontSize: "0.95rem",
                                fontWeight: 700,
                                letterSpacing: "0.02em",
                                lineHeight: 1.25,
                            }}
                        >
                            Identity verification
                        </Typography>
                        <Box
                            component="a"
                            href="https://didit.me"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={ACCOUNT_PAGE_KYC_BADGE_SX}
                        >
                            <Box component="span" sx={{ opacity: 0.7 }}>
                                Powered by
                            </Box>
                            <Box
                                component="span"
                                sx={{ fontWeight: 700, color: colors.text.primary }}
                            >
                                Didit
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {loading ? (
                    <CircularProgress size={14} sx={{ color: colors.text.muted }} />
                ) : isApproved ? (
                    <Box sx={{ ...ACCOUNT_PAGE_KYC_STATUS_CHIP_SX, flexShrink: 0 }}>
                        <VerifiedUserOutlined sx={{ fontSize: "0.9rem" }} />
                        Verified
                    </Box>
                ) : meta?.label ? (
                    <Typography
                        sx={{
                            color: colors.text.muted,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                        }}
                    >
                        {meta.label}
                    </Typography>
                ) : null}
            </Box>

            {isApproved ? (
                <Box sx={ACCOUNT_PAGE_KYC_SUCCESS_PANEL_SX}>
                    <Box
                        sx={{
                            width: "1.7rem",
                            height: "1.7rem",
                            borderRadius: "50%",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(34, 197, 94, 0.18)",
                            color: colors.state.success.main,
                        }}
                    >
                        <CheckRounded sx={{ fontSize: "1.05rem" }} />
                    </Box>
                    <Typography
                        sx={{
                            color: "rgba(187, 247, 208, 0.92)",
                            fontSize: "0.84rem",
                            lineHeight: 1.5,
                            fontWeight: 500,
                        }}
                    >
                        {meta.message}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Typography
                        sx={{
                            color: colors.text.secondary,
                            fontSize: "0.82rem",
                            lineHeight: 1.55,
                            letterSpacing: "0.01em",
                            mb: "1.1rem",
                        }}
                    >
                        Optional but recommended. Once your verification is approved, you&apos;ll earn a{" "}
                        <Box
                            component="span"
                            sx={{ color: colors.text.primary, fontWeight: 600 }}
                        >
                            Verified badge
                        </Box>{" "}
                        on your profile and increase trust in matchmaking.
                    </Typography>

                    {meta?.message ? (
                        <Typography
                            sx={{
                                color: colors.text.muted,
                                fontSize: "0.78rem",
                                lineHeight: 1.5,
                                mb: "1rem",
                            }}
                        >
                            {meta.message}
                        </Typography>
                    ) : null}

                    {error ? (
                        <Typography
                            sx={{
                                color: colors.state.danger.main,
                                fontSize: "0.78rem",
                                mb: "0.85rem",
                            }}
                        >
                            {error}
                        </Typography>
                    ) : null}

                    {!loading && ctaLabel ? (
                        <Box
                            component="button"
                            type="button"
                            onClick={handleStartVerification}
                            disabled={starting}
                            sx={ACCOUNT_PAGE_KYC_CTA_SX}
                        >
                            {starting ? (
                                <CircularProgress
                                    size={14}
                                    sx={{ color: colors.text.contrast }}
                                />
                            ) : null}
                            {starting ? "Starting…" : ctaLabel}
                        </Box>
                    ) : null}
                </>
            )}
        </Box>
    );
}

export default React.memo(AccountIdentityVerification);
